import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { licensesAPI, aiCreditsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const REMINDER_TYPES = [
  { value: 'trial_3_days', label: 'Trial - 3 Days Left', forTrial: true },
  { value: 'trial_1_day', label: 'Trial - 1 Day Left', forTrial: true },
  { value: 'trial_expired', label: 'Trial Expired', forTrial: true },
  { value: 'license_30_days', label: 'License - 30 Days Left', forTrial: false },
  { value: 'license_14_days', label: 'License - 14 Days Left', forTrial: false },
  { value: 'license_7_days', label: 'License - 7 Days Left', forTrial: false },
  { value: 'license_3_days', label: 'License - 3 Days Left', forTrial: false },
  { value: 'license_1_day', label: 'License - 1 Day Left', forTrial: false },
  { value: 'license_expired', label: 'License Expired', forTrial: false },
];

const LicenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [license, setLicense] = useState(null);
  const [credits, setCredits] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [extendDays, setExtendDays] = useState(30);
  const [extendSendEmail, setExtendSendEmail] = useState(true);
  const [extendCustomMessage, setExtendCustomMessage] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [machineId, setMachineId] = useState('');
  const [addCreditsForm, setAddCreditsForm] = useState({ credits: 100, reason: '' });
  const [reminderForm, setReminderForm] = useState({ reminder_type: '', force: false });
  const [editingExpiry, setEditingExpiry] = useState(false);
  const [newExpiryDate, setNewExpiryDate] = useState('');

  useEffect(() => {
    fetchLicense();
  }, [id]);

  const fetchLicense = async () => {
    try {
      const [licenseRes, creditsRes, remindersRes] = await Promise.all([
        licensesAPI.get(id),
        aiCreditsAPI.get(id).catch(() => ({ data: null })),
        licensesAPI.getReminders(id).catch(() => ({ data: [] }))
      ]);
      setLicense(licenseRes.data);
      setCredits(creditsRes.data);
      setReminders(remindersRes.data || []);

      // Auto-fill machine ID
      const licenseData = licenseRes.data;
      let autoMachineId = licenseData.machine_id || '';
      if (!autoMachineId && licenseData.activations?.length > 0) {
        const activeActivation = licenseData.activations.find(a => a.is_active);
        if (activeActivation) {
          autoMachineId = activeActivation.machine_id || '';
        } else {
          autoMachineId = licenseData.activations[0].machine_id || '';
        }
      }
      if (!autoMachineId && licenseData.customer) {
        try {
          const customerLicenses = await licensesAPI.list({ customer_id: licenseData.customer });
          for (const lic of (customerLicenses.data.results || customerLicenses.data || [])) {
            if (lic.id !== licenseData.id) {
              const otherLicense = await licensesAPI.get(lic.id);
              const otherActivations = otherLicense.data.activations || [];
              const activeAct = otherActivations.find(a => a.is_active);
              if (activeAct?.machine_id) {
                autoMachineId = activeAct.machine_id;
                break;
              } else if (otherActivations[0]?.machine_id) {
                autoMachineId = otherActivations[0].machine_id;
                break;
              }
            }
          }
        } catch (e) {
          console.log('Could not fetch customer licenses:', e);
        }
      }
      setMachineId(autoMachineId);
    } catch (err) {
      console.error('Failed to fetch license:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExtend = async () => {
    setActionLoading(true);
    try {
      const result = await licensesAPI.extend(id, extendDays, extendSendEmail, extendCustomMessage);
      await fetchLicense();
      setShowExtendModal(false);
      setExtendCustomMessage('');
      if (extendSendEmail && result?.data?.email_sent) {
        alert('License extended and email sent to customer.');
      } else if (extendSendEmail && result?.data?.email_error) {
        alert(`License extended but email failed: ${result.data.email_error}`);
      }
    } catch (err) {
      alert('Failed to extend license');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async () => {
    setActionLoading(true);
    try {
      await licensesAPI.revoke(id, revokeReason);
      await fetchLicense();
      setShowRevokeModal(false);
    } catch (err) {
      alert('Failed to revoke license');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!machineId.trim()) {
      alert('Machine ID is required');
      return;
    }
    setActionLoading(true);
    try {
      const response = await licensesAPI.generateKey(id, machineId);
      const { license_key, filename } = response.data;

      const blob = new Blob([license_key], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      await fetchLicense();
      setShowGenerateModal(false);
    } catch (err) {
      alert('Failed to generate license key');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddCredits = async () => {
    setActionLoading(true);
    try {
      await aiCreditsAPI.addCredits(id, addCreditsForm);
      const creditsRes = await aiCreditsAPI.get(id);
      setCredits(creditsRes.data);
      setShowAddCreditsModal(false);
      setAddCreditsForm({ credits: 100, reason: '' });
    } catch (err) {
      console.error('Failed to add credits:', err);
      alert(err.response?.data?.error || 'Failed to add credits');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendReminder = async () => {
    if (!reminderForm.reminder_type) {
      alert('Please select a reminder type');
      return;
    }
    setActionLoading(true);
    try {
      await licensesAPI.sendReminder(id, reminderForm);
      const remindersRes = await licensesAPI.getReminders(id);
      setReminders(remindersRes.data || []);
      setShowReminderModal(false);
      setReminderForm({ reminder_type: '', force: false });
      alert('Renewal reminder sent successfully!');
    } catch (err) {
      console.error('Failed to send reminder:', err);
      alert(err.response?.data?.error || 'Failed to send reminder');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEmailLicense = async () => {
    if (!window.confirm(`Send license file to ${license.customer_email}?`)) {
      return;
    }
    setActionLoading(true);
    try {
      await licensesAPI.emailLicense(id);
      alert(`License sent successfully to ${license.customer_email}`);
    } catch (err) {
      console.error('Failed to email license:', err);
      alert(err.response?.data?.error || 'Failed to email license');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateExpiry = async () => {
    if (!newExpiryDate) return;
    setActionLoading(true);
    try {
      await licensesAPI.update(id, { expiry_date: new Date(newExpiryDate + 'T23:59:59Z').toISOString() });
      await fetchLicense();
      setEditingExpiry(false);
    } catch (err) {
      console.error('Failed to update expiry date:', err);
      alert(err.response?.data?.error || 'Failed to update expiry date');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendThankYou = async () => {
    if (!window.confirm(`Send thank you email to ${license.customer_email}?`)) {
      return;
    }
    setActionLoading(true);
    try {
      await licensesAPI.sendThankYou(id);
      alert(`Thank you email sent to ${license.customer_email}`);
    } catch (err) {
      console.error('Failed to send thank you:', err);
      alert(err.response?.data?.error || 'Failed to send thank you email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await licensesAPI.delete(id);
      navigate('/dashboard/licenses');
    } catch (err) {
      console.error('Failed to delete license:', err);
      alert(err.response?.data?.error || 'Failed to delete license');
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  if (!license) {
    return <div className="error-container"><p>License not found</p></div>;
  }

  const getStatusBadge = () => {
    if (license.is_expired) return <span className="badge badge-danger">Expired</span>;
    if (license.status === 'revoked') return <span className="badge badge-danger">Revoked</span>;
    if (license.is_expiring_soon) return <span className="badge badge-warning">Expiring Soon</span>;
    if (license.status === 'active') return <span className="badge badge-success">Active</span>;
    return <span className="badge badge-gray">{license.status}</span>;
  };

  const getCreditStatusColor = () => {
    if (!credits?.credit_info) return 'var(--gray-500)';
    const { total_available, monthly_quota, quota_remaining } = credits.credit_info;
    const percentUsed = monthly_quota > 0 ? ((monthly_quota - quota_remaining) / monthly_quota) * 100 : 0;
    if (total_available <= 0) return 'var(--danger)';
    if (total_available <= 10 || percentUsed >= 80) return 'var(--warning)';
    return 'var(--success)';
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">License Details</h1>
          <p className="page-description">
            <Link to={`/dashboard/customers/${license.customer}`} style={{ color: 'var(--primary)' }}>
              {license.customer_name}
            </Link>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {license.status !== 'revoked' && (
            <>
              <button className="btn btn-secondary" onClick={() => setShowGenerateModal(true)}>
                Generate Key
              </button>
              {license.license_key && (
                <button
                  className="btn btn-secondary"
                  onClick={handleEmailLicense}
                  disabled={actionLoading}
                >
                  Email License
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setShowExtendModal(true)}>
                Extend
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleSendThankYou}
                disabled={actionLoading}
              >
                Send Thank You
              </button>
              <button className="btn btn-primary" onClick={() => setShowReminderModal(true)}>
                Send Reminder
              </button>
              {isAdmin && (
                <button className="btn btn-danger" onClick={() => setShowRevokeModal(true)}>
                  Revoke
                </button>
              )}
            </>
          )}
          {isAdmin && (
            <button className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
              Delete
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* License Info */}
        <div className="card">
          <h3 style={{ marginBottom: '1rem', fontWeight: 600 }}>License Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Status</span>
              <p>{getStatusBadge()}</p>
            </div>
            <div>
              <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Type</span>
              <p style={{ textTransform: 'capitalize' }}>{license.license_type}</p>
            </div>
            <div>
              <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Issue Date</span>
              <p>{format(new Date(license.issue_date), 'MMMM d, yyyy')}</p>
            </div>
            <div>
              <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Expiry Date</span>
              {editingExpiry ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                  <input
                    type="date"
                    className="form-input"
                    value={newExpiryDate}
                    onChange={(e) => setNewExpiryDate(e.target.value)}
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem', width: 'auto' }}
                  />
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleUpdateExpiry}
                    disabled={actionLoading || !newExpiryDate}
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                  >
                    Save
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setEditingExpiry(false)}
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {format(new Date(license.expiry_date), 'MMMM d, yyyy')}
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setNewExpiryDate(format(new Date(license.expiry_date), 'yyyy-MM-dd'));
                        setEditingExpiry(true);
                      }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--primary)', fontSize: '0.8rem', padding: '0'
                      }}
                      title="Edit expiry date"
                    >
                      Edit
                    </button>
                  )}
                </p>
              )}
            </div>
            <div>
              <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Days Remaining</span>
              <p style={{ color: license.is_expired ? 'var(--danger)' : license.days_until_expiry <= 30 ? 'var(--warning)' : 'inherit' }}>
                {license.is_expired ? 'Expired' : `${license.days_until_expiry} days`}
              </p>
            </div>
            <div>
              <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Max Users</span>
              <p>{license.max_users}</p>
            </div>
            <div>
              <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Activations</span>
              <p>{license.activation_count} / {license.max_activations}</p>
            </div>
            <div>
              <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Machine ID</span>
              <p style={{ wordBreak: 'break-all', fontSize: '0.875rem' }}>{license.machine_id || 'Not set'}</p>
            </div>
          </div>
        </div>

        {/* AI Credits Card */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              AI Credits
            </h3>
            {isAdmin && (
              <button className="btn btn-sm btn-primary" onClick={() => setShowAddCreditsModal(true)}>
                Add Credits
              </button>
            )}
          </div>

          {credits?.credit_info ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: getCreditStatusColor() }}>
                    {credits.credit_info.total_available?.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Available Credits</div>
                </div>
                <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'var(--primary)' }}>
                    {credits.credit_info.used_this_period?.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                    Used {credits.credit_info.is_trial ? 'Today' : 'This Month'}
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Monthly Quota:</span>
                  <span>{credits.credit_info.monthly_quota?.toLocaleString()} credits</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Quota Remaining:</span>
                  <span>{credits.credit_info.quota_remaining?.toLocaleString()} credits</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Bonus Credits:</span>
                  <span style={{ color: credits.bonus_credits > 0 ? 'var(--success)' : 'inherit' }}>
                    {credits.bonus_credits > 0 ? `+${credits.bonus_credits.toLocaleString()}` : '0'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Period Type:</span>
                  <span className={`badge badge-${credits.credit_info.is_trial ? 'warning' : 'info'}`}>
                    {credits.credit_info.is_trial ? 'Daily (Trial)' : 'Monthly'}
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                  <span>Usage</span>
                  <span>
                    {Math.round((credits.credit_info.used_this_period / credits.credit_info.monthly_quota) * 100)}%
                  </span>
                </div>
                <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min((credits.credit_info.used_this_period / credits.credit_info.monthly_quota) * 100, 100)}%`,
                      background: getCreditStatusColor(),
                      borderRadius: '4px',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: '2rem' }}>
              No credit information available
            </p>
          )}
        </div>
      </div>

      {/* Activation History */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem', fontWeight: 600 }}>Activation History</h3>
        {license.activations?.length === 0 ? (
          <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: '2rem' }}>
            No activations yet
          </p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Machine ID</th>
                  <th>IP</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {license.activations?.map((activation) => (
                  <tr key={activation.id}>
                    <td style={{ fontSize: '0.8rem', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {activation.machine_id}
                    </td>
                    <td>{activation.ip_address || 'N/A'}</td>
                    <td>{format(new Date(activation.activated_at), 'MMM d, yyyy')}</td>
                    <td>
                      <span className={`badge badge-${activation.is_active ? 'success' : 'gray'}`}>
                        {activation.is_active ? 'Active' : 'Deactivated'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sent Reminders History */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          Sent Renewal Reminders
        </h3>
        {reminders.length === 0 ? (
          <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: '2rem' }}>
            No reminders sent yet
          </p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Sent To</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {reminders.map((reminder) => (
                  <tr key={reminder.id}>
                    <td>
                      {REMINDER_TYPES.find(t => t.value === reminder.reminder_type)?.label || reminder.reminder_type}
                    </td>
                    <td>{reminder.sent_to}</td>
                    <td>{format(new Date(reminder.sent_at), 'MMM d, yyyy HH:mm')}</td>
                    <td>
                      <span className={`badge badge-${reminder.success ? 'success' : 'danger'}`}>
                        {reminder.success ? 'Sent' : 'Failed'}
                      </span>
                      {!reminder.success && reminder.error_message && (
                        <span title={reminder.error_message} style={{ marginLeft: '0.5rem', cursor: 'help', color: 'var(--danger)' }}>
                          ?
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showExtendModal && (
        <Modal title="Extend License" onClose={() => setShowExtendModal(false)}>
          <div className="form-group">
            <label className="form-label">Days to extend</label>
            <input
              type="number"
              className="form-input"
              value={extendDays}
              onChange={(e) => setExtendDays(parseInt(e.target.value))}
              min="1"
              max="3650"
            />
          </div>
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label className="form-label">Custom message to customer (optional)</label>
            <textarea
              className="form-input"
              rows="3"
              value={extendCustomMessage}
              onChange={(e) => setExtendCustomMessage(e.target.value)}
              placeholder="e.g. We've extended your trial so you can complete your evaluation..."
              style={{ resize: 'vertical' }}
            />
          </div>
          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={extendSendEmail}
                onChange={(e) => setExtendSendEmail(e.target.checked)}
              />
              Send email notification to customer
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button className="btn btn-secondary" onClick={() => setShowExtendModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleExtend} disabled={actionLoading}>
              {actionLoading ? 'Extending...' : 'Extend'}
            </button>
          </div>
        </Modal>
      )}

      {showRevokeModal && (
        <Modal title="Revoke License" onClose={() => setShowRevokeModal(false)}>
          <div className="form-group">
            <label className="form-label">Reason (optional)</label>
            <textarea
              className="form-input"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              rows={3}
              placeholder="Enter reason for revocation..."
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button className="btn btn-secondary" onClick={() => setShowRevokeModal(false)}>Cancel</button>
            <button className="btn btn-danger" onClick={handleRevoke} disabled={actionLoading}>
              {actionLoading ? 'Revoking...' : 'Revoke License'}
            </button>
          </div>
        </Modal>
      )}

      {showGenerateModal && (
        <Modal title="Generate License Key" onClose={() => setShowGenerateModal(false)}>
          <div className="form-group">
            <label className="form-label">Machine ID</label>
            <input
              type="text"
              className="form-input"
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
              placeholder="Enter the customer's machine ID..."
            />
            <small style={{ color: 'var(--gray-500)', marginTop: '0.25rem', display: 'block' }}>
              The machine ID is shown on the customer's activation page
            </small>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button className="btn btn-secondary" onClick={() => setShowGenerateModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleGenerateKey} disabled={actionLoading}>
              {actionLoading ? 'Generating...' : 'Generate & Download'}
            </button>
          </div>
        </Modal>
      )}

      {showAddCreditsModal && (
        <Modal title="Add AI Credits" onClose={() => setShowAddCreditsModal(false)}>
          <div className="form-group">
            <label className="form-label">Credits to Add *</label>
            <input
              type="number"
              className="form-input"
              value={addCreditsForm.credits}
              onChange={(e) => setAddCreditsForm({ ...addCreditsForm, credits: parseInt(e.target.value) || 0 })}
              min="1"
              max="100000"
            />
            <small style={{ color: 'var(--gray-500)' }}>1 credit = 1,000 tokens</small>
          </div>
          <div className="form-group">
            <label className="form-label">Reason (optional)</label>
            <textarea
              className="form-input"
              value={addCreditsForm.reason}
              onChange={(e) => setAddCreditsForm({ ...addCreditsForm, reason: e.target.value })}
              rows={2}
              placeholder="e.g., Promotional credits, compensation, etc."
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button className="btn btn-secondary" onClick={() => setShowAddCreditsModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleAddCredits} disabled={actionLoading || addCreditsForm.credits <= 0}>
              {actionLoading ? 'Adding...' : `Add ${addCreditsForm.credits} Credits`}
            </button>
          </div>
        </Modal>
      )}

      {showReminderModal && (
        <Modal title="Send Renewal Reminder" onClose={() => setShowReminderModal(false)}>
          <div className="form-group">
            <label className="form-label">Reminder Type *</label>
            <select
              className="form-input"
              value={reminderForm.reminder_type}
              onChange={(e) => setReminderForm({ ...reminderForm, reminder_type: e.target.value })}
            >
              <option value="">Select a reminder type...</option>
              {REMINDER_TYPES
                .filter(t => license.license_type === 'trial' ? t.forTrial : !t.forTrial)
                .map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))
              }
            </select>
            <small style={{ color: 'var(--gray-500)', marginTop: '0.25rem', display: 'block' }}>
              A professionally formatted email will be sent to {license.customer_email || 'the customer'}
            </small>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="forceReminder"
              checked={reminderForm.force}
              onChange={(e) => setReminderForm({ ...reminderForm, force: e.target.checked })}
            />
            <label htmlFor="forceReminder" style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
              Resend even if already sent
            </label>
          </div>
          {reminders.length > 0 && (
            <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem' }}>
              <small style={{ color: 'var(--gray-500)' }}>
                Previously sent: {reminders.filter(r => r.success).map(r =>
                  REMINDER_TYPES.find(t => t.value === r.reminder_type)?.label || r.reminder_type
                ).join(', ') || 'None'}
              </small>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button className="btn btn-secondary" onClick={() => setShowReminderModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSendReminder} disabled={actionLoading || !reminderForm.reminder_type}>
              {actionLoading ? 'Sending...' : 'Send Reminder'}
            </button>
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal title="Delete License" onClose={() => setShowDeleteModal(false)}>
          <p style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>
            This permanently deletes this license and all of its activation history.
            This <strong>cannot be undone</strong>. The customer ({license.customer_name})
            will no longer have this license.
          </p>
          <p style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>
            If you only want to invalidate it, use <strong>Revoke</strong> instead.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
            <button className="btn btn-danger" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete License'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Simple Modal component
const Modal = ({ title, children, onClose }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)'
    }} onClick={onClose} />
    <div style={{
      position: 'relative', background: 'white', borderRadius: '0.75rem',
      padding: '1.5rem', width: '100%', maxWidth: '400px', margin: '1rem'
    }}>
      <h3 style={{ marginBottom: '1rem', fontWeight: 600 }}>{title}</h3>
      {children}
    </div>
  </div>
);

export default LicenseDetail;
